/**
 * The solids the cursor's line of sight can meet (input/aimVolume.ts): everything on the map a player might
 * shoot, throw at or fire a LAW into - every prop that can be destroyed or is a target - and every machine
 * and vehicle, each as tall as it is drawn. Men are not here: `aimTargets` hands back a man's feet.
 *
 * Found by the aim audit (tests/unit/aimAudit.test.ts, 2026-09-23): with the cursor plainly on a truck, a
 * watchtower, a wall or a comms mast, 27 target and weapon pairs missed from some bearing.
 */

import type { AimVolume } from '../input/aimVolume';
import type { Palette } from '../render/palette';
import { machineDrawnHeight, propDrawnHeight } from '../render/world/drawnSize';
import { propScale } from '../render/world/propModels';
import { hullRadius, isAutomated, isVehicle } from '../sim/enemies/specials/kinds';
import { hullSpine } from '../sim/enemies/specials/vehicleHull';
import { collidersFor, PROPS } from '../sim/props';
import type { Enemy, MissionDef, PropState } from '../sim/types';

type GroundY = (x: number, z: number) => number;

/** One prop's solids, kept with its id so a destroyed prop can be dropped without rebuilding the rest. */
export interface PropVolume {
  propId: number;
  volume: AimVolume;
}

/** Built once per mission: the destroyable and target props, as drawn. */
export function propVolumes(def: MissionDef, palette: Palette, groundY: GroundY): PropVolume[] {
  const out: PropVolume[] = [];
  for (const p of def.props) {
    if (PROPS[p.kind].durability === 'none' && p.target !== true) continue;
    const height = propDrawnHeight(p.kind, palette) * propScale(p);
    for (const c of collidersFor(p)) {
      out.push({
        propId: p.id,
        volume: { shape: c.shape, x: c.x, z: c.z, r: c.r, hx: c.hx, hz: c.hz, y: groundY(c.x, c.z), height },
      });
    }
  }
  return out;
}

/** The props' solids still standing. */
export function standingVolumes(all: readonly PropVolume[], props: readonly PropState[]): AimVolume[] {
  const down = new Set<number>();
  for (const p of props) if (p.destroyed) down.add(p.id);
  const out: AimVolume[] = [];
  for (const v of all) if (!down.has(v.propId)) out.push(v.volume);
  return out;
}

/**
 * Machines and vehicles where they are now: a machine as the round hull the sim hits it by, a vehicle as its
 * capsule laid in hull-sized discs along its spine, exactly as `rocketStrikes` samples it.
 */
export function machineVolumes(enemies: readonly Enemy[], palette: Palette, groundY: GroundY): AimVolume[] {
  const out: AimVolume[] = [];
  for (const e of enemies) {
    if (e.state === 'dead' || !isAutomated(e.kind)) continue;
    const r = hullRadius(e.kind);
    const height = machineDrawnHeight(e.kind, palette);
    const disc = (x: number, z: number): AimVolume => ({
      shape: 'circle', x, z, r, hx: 0, hz: 0, y: groundY(x, z), height,
    });
    if (!isVehicle(e.kind)) {
      out.push(disc(e.x, e.z));
      continue;
    }
    const s = hullSpine(e.kind, e.x, e.z, e.facing);
    const span = Math.hypot(s.bx - s.ax, s.bz - s.az);
    const steps = Math.max(1, Math.ceil(span / r));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      out.push(disc(s.ax + (s.bx - s.ax) * t, s.az + (s.bz - s.az) * t));
    }
  }
  return out;
}
